/**
 * Cliente y controlador de la limpieza de datos antiguos.
 *
 * La operación ya no es síncrona: el POST devuelve un `operacion_id` de
 * inmediato y el progreso se consulta con `GET /estado`. Antes el POST
 * bloqueaba la petición durante todo el proceso —3 h 30 en el caso real—, lo
 * que garantizaba un timeout del proxy mucho antes de terminar.
 *
 * La lógica de polling vive aquí, fuera de React, por dos razones: se puede
 * probar sin montar componentes ni añadir dependencias, y el componente queda
 * como una capa de render sin estado propio que pueda desincronizarse.
 */
import { ApiError, fetchAPI } from './apiClient';

// ═══════════════════════════════════════════════════════════════════
// Contrato del backend
// ═══════════════════════════════════════════════════════════════════

export type EstadoLimpieza =
  | 'PREPARANDO'
  | 'PREPARANDO_SNAPSHOTS'
  | 'LISTO_PARA_APLICAR'
  | 'APLICANDO'
  | 'SIMULACRO_COMPLETADO'
  | 'COMPLETADO'
  | 'CANCELACION_SOLICITADA'
  | 'CANCELADO'
  | 'FALLIDO';

export interface OperacionLimpiezaEstado {
  operacion_id: string;
  estado: EstadoLimpieza;
  es_simulacro: boolean;
  etapa_actual: string | null;
  etapa_numero: number | null;
  etapas_totales: number;
  registros_procesados: number;
  registros_totales: number;
  porcentaje: number;
  mensaje: string | null;
  error: string | null;
  mensaje_error: string | null;
  iniciada_at: string | null;
  actualizada_at: string | null;
  finalizada_at: string | null;
  segundos_transcurridos: number;
  activa: boolean;
  cancelable: boolean;
  punto_de_no_retorno: boolean;
  fecha_limite: string | null;
}

export interface InicioOperacion {
  operacion_id: string;
  estado: EstadoLimpieza;
  simulacro: boolean;
  fecha_limite: string;
}

/** Cuerpo PLANO del 409: `detail` y `operacion` al mismo nivel. */
export interface ConflictoOperacionActiva {
  detail: string;
  operacion: {
    operacion_id: string;
    estado: EstadoLimpieza;
    etapa_actual: string | null;
    porcentaje: number;
  };
}

export interface RespuestaCancelacion {
  ok: boolean;
  estado: EstadoLimpieza;
  motivo: string;
}

// ═══════════════════════════════════════════════════════════════════
// Constantes
// ═══════════════════════════════════════════════════════════════════

/**
 * El preview recorre todo el historial con consultas agregadas: en una base
 * grande puede pasar de 30 s. Se le da su propio timeout en lugar de subir
 * `API_TIMEOUT`, que protege al resto de la aplicación de peticiones colgadas.
 */
export const TIMEOUT_PREVIEW_MS = 5 * 60 * 1000;

export const INTERVALO_POLLING_MS = 2000;

export const FRASE_CONFIRMACION_BACKEND = 'LIMPIAR DATOS ANTIGUOS';

export const ESTADOS_FINALES: readonly EstadoLimpieza[] = [
  'SIMULACRO_COMPLETADO', 'COMPLETADO', 'CANCELADO', 'FALLIDO',
];

export function esEstadoFinal(estado: EstadoLimpieza): boolean {
  return ESTADOS_FINALES.includes(estado);
}

/**
 * Texto por estado. Vive en el frontend porque es copia de interfaz, pero
 * NUNCA inventa progreso: si el backend no dice COMPLETADO, aquí no se dice.
 */
export const TEXTO_ESTADO: Record<EstadoLimpieza, string> = {
  PREPARANDO: 'Preparando plan de limpieza',
  PREPARANDO_SNAPSHOTS: 'Preparando resúmenes históricos',
  LISTO_PARA_APLICAR: 'Preparación completada',
  APLICANDO: 'Aplicando limpieza. Ya no puede cancelarse.',
  SIMULACRO_COMPLETADO: 'Simulacro completado correctamente',
  COMPLETADO: 'Limpieza completada correctamente',
  CANCELACION_SOLICITADA: 'Cancelación solicitada',
  CANCELADO: 'Operación cancelada sin aplicar la limpieza',
  FALLIDO: 'La operación falló',
};

export function textoDeEstado(e: OperacionLimpiezaEstado): string {
  if (e.estado === 'FALLIDO') {
    return e.mensaje_error || e.error || TEXTO_ESTADO.FALLIDO;
  }
  return TEXTO_ESTADO[e.estado] ?? e.estado;
}

// ═══════════════════════════════════════════════════════════════════
// Llamadas
// ═══════════════════════════════════════════════════════════════════

export function obtenerPreview<T>(): Promise<T> {
  return fetchAPI<T>('/saldos/limpieza-datos/preview',
                     { timeoutMs: TIMEOUT_PREVIEW_MS });
}

export function iniciarSimulacro(): Promise<InicioOperacion> {
  return fetchAPI<InicioOperacion>('/saldos/limpieza-datos', {
    method: 'POST',
    body: JSON.stringify({ simulacro: true }),
  });
}

export function iniciarLimpiezaReal(): Promise<InicioOperacion> {
  return fetchAPI<InicioOperacion>('/saldos/limpieza-datos', {
    method: 'POST',
    body: JSON.stringify({ confirmacion: FRASE_CONFIRMACION_BACKEND }),
  });
}

export function consultarEstado(
  operacionId?: string,
): Promise<OperacionLimpiezaEstado> {
  const q = operacionId ? `?operacion_id=${encodeURIComponent(operacionId)}` : '';
  return fetchAPI<OperacionLimpiezaEstado>(`/saldos/limpieza-datos/estado${q}`);
}

export function cancelarOperacion(
  operacionId: string,
): Promise<RespuestaCancelacion> {
  return fetchAPI<RespuestaCancelacion>('/saldos/limpieza-datos/cancelar', {
    method: 'POST',
    body: JSON.stringify({ operacion_id: operacionId }),
  });
}

/**
 * Extrae la operación de un 409 estructurado, o `null` si no lo es.
 *
 * Sin esto, un 409 por operación activa se mostraría como un error genérico y
 * el usuario quedaría sin forma de ver la limpieza que ya está corriendo — ni
 * de saber cuándo termina.
 */
export function operacionDelConflicto(
  error: unknown,
): ConflictoOperacionActiva['operacion'] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const cuerpo = error.payload as Partial<ConflictoOperacionActiva> | null;
  const op = cuerpo?.operacion;
  if (!op || typeof op.operacion_id !== 'string') return null;
  return op;
}

/** ¿Es el 409 de «ya pasó el punto de no retorno» al cancelar? */
export function esConflictoDeCancelacion(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

// ═══════════════════════════════════════════════════════════════════
// Controlador de polling
// ═══════════════════════════════════════════════════════════════════

export interface OpcionesSeguimiento {
  onEstado: (estado: OperacionLimpiezaEstado) => void;
  onFinal: (estado: OperacionLimpiezaEstado) => void;
  /** Fallo TEMPORAL del GET: se avisa y se reintenta. `null` al recuperarse. */
  onErrorTemporal: (mensaje: string | null) => void;
  intervaloMs?: number;
  temporizador?: {
    set: (fn: () => void, ms: number) => unknown;
    clear: (id: unknown) => void;
  };
}

/**
 * Sigue una operación hasta que llegue a un estado final.
 *
 * Reglas que este objeto garantiza, y que son la razón de que exista:
 *
 *   · UN solo temporizador vivo. Cada tic programa el siguiente solo después
 *     de que la consulta anterior terminó, así que dos respuestas lentas no
 *     pueden solaparse ni acumular timers.
 *   · NUNCA reenvía el POST. Si el GET falla, reintenta EL GET. Reintentar el
 *     POST podría lanzar una segunda limpieza.
 *   · `detener()` es idempotente y deja de programar tics: al desmontar el
 *     componente no queda ningún temporizador huérfano.
 */
export class SeguimientoOperacion {
  private timer: unknown = null;
  private detenido = false;
  private readonly intervalo: number;
  private readonly reloj: NonNullable<OpcionesSeguimiento['temporizador']>;

  constructor(
    private readonly operacionId: string,
    private readonly opciones: OpcionesSeguimiento,
  ) {
    this.intervalo = opciones.intervaloMs ?? INTERVALO_POLLING_MS;
    this.reloj = opciones.temporizador ?? {
      set: (fn, ms) => setTimeout(fn, ms),
      clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
    };
  }

  /** Consulta una vez de inmediato y luego programa el ciclo. */
  async iniciar(): Promise<void> {
    await this.tic();
  }

  detener(): void {
    this.detenido = true;
    if (this.timer !== null) {
      this.reloj.clear(this.timer);
      this.timer = null;
    }
  }

  get activo(): boolean {
    return !this.detenido;
  }

  private async tic(): Promise<void> {
    if (this.detenido) return;
    try {
      const estado = await consultarEstado(this.operacionId);
      if (this.detenido) return;
      this.opciones.onErrorTemporal(null);
      this.opciones.onEstado(estado);
      if (esEstadoFinal(estado.estado)) {
        this.detener();
        this.opciones.onFinal(estado);
        return;
      }
    } catch (e) {
      if (this.detenido) return;
      // Un fallo de red no significa que la operación haya fallado: el
      // servidor sigue trabajando. Se avisa y se vuelve a consultar.
      const mensaje = e instanceof Error ? e.message : 'Error de conexión';
      this.opciones.onErrorTemporal(
        `No se pudo consultar el progreso (${mensaje}). Reintentando…`);
    }
    this.programarSiguiente();
  }

  private programarSiguiente(): void {
    if (this.detenido || this.timer !== null) return;
    this.timer = this.reloj.set(() => {
      this.timer = null;
      void this.tic();
    }, this.intervalo);
  }
}

/**
 * Busca una operación activa al montar la pantalla.
 *
 * Sin esto, recargar la página durante una limpieza dejaría al usuario sin
 * saber que sigue corriendo — y el botón le respondería con un 409 críptico.
 * Un 404 significa «no hay ninguna», que es normal, no un error.
 */
export async function buscarOperacionActiva(): Promise<OperacionLimpiezaEstado | null> {
  try {
    const estado = await consultarEstado();
    return estado.activa ? estado : null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** Progreso a mostrar. Nunca inventa un porcentaje ni divide por cero. */
export function progresoVisible(e: OperacionLimpiezaEstado): {
  porcentaje: number;
  registros: string | null;
  etapa: string;
} {
  const porcentaje = Math.max(0, Math.min(100, Number(e.porcentaje) || 0));
  const etapa = e.etapa_numero && e.etapas_totales
    ? `Etapa ${e.etapa_numero} de ${e.etapas_totales}`
    : '';
  // `registros_totales === 0` es normal en las fases de preparación: no hay
  // nada que contar todavía. Se omite la fracción en vez de mostrar 0/0.
  const registros = e.registros_totales > 0
    ? `${e.registros_procesados.toLocaleString('es-CR')} / ` +
      `${e.registros_totales.toLocaleString('es-CR')}`
    : null;
  return { porcentaje, registros, etapa };
}

export function tiempoTranscurrido(segundos: number): string {
  const s = Math.max(0, Math.round(segundos));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h} h ${m % 60} min`;
  if (m > 0) return `${m} min ${s % 60} s`;
  return `${s} s`;
}
